import React, { useRef, useState, useEffect, Fragment } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';
import { searchUser } from 'modules/user/user.actions';
import { View, StyledTextareaAutocomplete } from 'modules/styled/web';
import { CTALink } from 'modules/styled';
import AvatarBox from 'modules/user/avatarbox.component';
import CommentBody from 'modules/comment/commentBody';
import { useCommunity } from 'modules/community/community.selectors';

TextAreaWithMention.propTypes = {
  textArea: PropTypes.shape({ current: PropTypes.any }),
  value: PropTypes.string,
  autoFocus: PropTypes.bool,
  leftPadding: PropTypes.number,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  setFocused: PropTypes.func,
  withPreview: PropTypes.bool,
  children: PropTypes.node,
  placeholder: PropTypes.string,
  minheight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ImageUpload: PropTypes.func,
  PasteTextButton: PropTypes.func,
};

export default function TextAreaWithMention({
  textArea,
  value,
  autoFocus,
  leftPadding,
  onChange,
  onBlur,
  setFocused,
  children,
  withPreview,
  placeholder,
  minheight,
  ImageUpload,
  PasteTextButton = () => null,
}) {
  const dispatch = useDispatch();
  const innerTextAreaRef = useRef();
  const [mode, setMode] = useState('write');
  const { allowImages } = useCommunity() || {};

  const showWrite = mode === 'write';
  const showPreview = mode === 'preview';

  useEffect(() => {
    return () => dispatch(searchUser());
  }, []); // eslint-disable-line

  return (
    <View sx={{ flexDirection: 'column' }}>
      {withPreview && (
        <View sx={{ mb: 1 }}>
          <View sx={{ mr: 2 }}>
            <CTALink
              sx={{
                color: 'black',
                textDecorationLine: showWrite ? 'underline' : 'none',
              }}
              onPress={() => setMode('write')}
            >
              Write
            </CTALink>
          </View>
          <View>
            <CTALink
              sx={{
                color: 'black',
                textDecorationLine: showPreview ? 'underline' : 'none',
              }}
              onPress={() => setMode('preview')}
            >
              Preview Markdown
            </CTALink>
          </View>
        </View>
      )}
      {showWrite && (
        <View
          sx={{
            position: 'relative',
            flex: 1,
            flexDirection: 'column',
            alignItems: 'stretch',
          }}
        >
          <StyledTextareaAutocomplete
            containerStyle={{
              display: 'flex',
              flex: 'auto',
              flexDirection: 'column',
            }}
            listStyle={{
              display: 'flex',
              flexWrap: 'wrap',
              listStyleType: 'none',
              padding: 0,
            }}
            style={{ flex: 'auto' }}
            innerRef={(c) => (textArea.current = c)}
            ref={(c) => (innerTextAreaRef.current = c)}
            rows={2}
            placeholder={placeholder}
            value={value}
            // onKeyDown={handleKeydown}
            onChange={onChange}
            sx={{ m: 0, flex: 1, pl: leftPadding, minHeight: minheight + 0.0001 }}
            autoFocus={!!autoFocus}
            onFocus={() => {
              setFocused && setFocused(true);
              setTimeout(() => {
                const p = innerTextAreaRef.current.getCaretPosition();
                if (p === 0) innerTextAreaRef.current.setCaretPosition(value.length);
              });
            }}
            // bug with autocomplete
            // https://github.com/webscopeio/react-textarea-autocomplete/issues/178
            onBlur={(e) => {
              e.type === 'blur' && onBlur && onBlur(e);
              // e.type === 'blur' && setFocused && setFocused(false);
            }}
            textAreaComponent={{ component: TextareaAutosize, ref: 'inputRef' }}
            loadingComponent={() => <span>Loading</span>}
            trigger={{
              '@': {
                dataProvider: async (token) => {
                  const users = await dispatch(searchUser(token));
                  return users.map((u) => ({
                    user: u,
                    name: u.handle,
                  }));
                },
                component: Item,
                output: (item) => '@' + item.name,
              },
            }}
          />
          {children}
          <PasteTextButton />
        </View>
      )}

      {showPreview && (
        <View
          sx={{
            minHeight: minheight || 9,
            p: 2,
            pt: 1,
            variant: 'borders.input',
          }}
        >
          <CommentBody noLink comment={{ body: value }} noPostLink />
        </View>
      )}
      {allowImages ? (
        <Fragment>
          <View sx={{ mt: 1 }} />
          <ImageUpload />
        </Fragment>
      ) : null}
      <View sx={{ mt: 2 }} />
    </View>
  );
}

Item.propTypes = {
  entity: { user: PropTypes.object },
};

function Item({ entity }) {
  return (
    <View
      sx={{
        p: 1,
        pr: 2,
        cursor: 'pointer',
        '&:hover': {
          bg: 'lightGrey',
        },
      }}
    >
      <AvatarBox user={entity.user} noLink />
    </View>
  );
}
